import { ChevronDown, Zap, Trophy, Users } from "lucide-react";

// Football silhouette SVG component with neon glow
const FootballSilhouette = () => (
  <svg 
    viewBox="0 0 400 240" 
    className="absolute w-[120%] h-auto left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
    style={{ 
      filter: 'drop-shadow(0 0 20px rgba(0, 243, 255, 0.5)) drop-shadow(0 0 40px rgba(255, 0, 127, 0.3))',
      zIndex: 0
    }}
  >
    {/* Football outline with gradient stroke */}
    <defs>
      <linearGradient id="footballGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00f3ff" />
        <stop offset="50%" stopColor="#ff007f" />
        <stop offset="100%" stopColor="#00f3ff" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    
    {/* Football shape - pointed oval */}
    <ellipse 
      cx="200" 
      cy="120" 
      rx="180" 
      ry="100" 
      fill="none" 
      stroke="url(#footballGradient)" 
      strokeWidth="3"
      filter="url(#glow)"
      className="animate-pulse"
      style={{ animationDuration: '3s' }}
    />
    
    {/* Football laces - center vertical line */}
    <line 
      x1="200" y1="40" 
      x2="200" y2="200" 
      stroke="url(#footballGradient)" 
      strokeWidth="2"
      filter="url(#glow)"
      opacity="0.6"
    />
    
    {/* Football laces - horizontal stripes */}
    <line x1="175" y1="70" x2="225" y2="70" stroke="#00f3ff" strokeWidth="2" filter="url(#glow)" opacity="0.8"/>
    <line x1="170" y1="95" x2="230" y2="95" stroke="#ff007f" strokeWidth="2" filter="url(#glow)" opacity="0.8"/>
    <line x1="168" y1="120" x2="232" y2="120" stroke="#00f3ff" strokeWidth="2" filter="url(#glow)" opacity="0.8"/>
    <line x1="170" y1="145" x2="230" y2="145" stroke="#ff007f" strokeWidth="2" filter="url(#glow)" opacity="0.8"/>
    <line x1="175" y1="170" x2="225" y2="170" stroke="#00f3ff" strokeWidth="2" filter="url(#glow)" opacity="0.8"/>
    
    {/* Corner accents */}
    <path 
      d="M 40 120 Q 40 60 100 40" 
      fill="none" 
      stroke="#00f3ff" 
      strokeWidth="2"
      filter="url(#glow)"
      opacity="0.5"
    />
    <path 
      d="M 360 120 Q 360 60 300 40" 
      fill="none" 
      stroke="#ff007f" 
      strokeWidth="2"
      filter="url(#glow)"
      opacity="0.5"
    />
    <path 
      d="M 40 120 Q 40 180 100 200" 
      fill="none" 
      stroke="#ff007f" 
      strokeWidth="2"
      filter="url(#glow)"
      opacity="0.5"
    />
    <path 
      d="M 360 120 Q 360 180 300 200" 
      fill="none" 
      stroke="#00f3ff" 
      strokeWidth="2"
      filter="url(#glow)"
      opacity="0.5"
    />
  </svg>
);

const HeroSection = () => {
  const scrollToRoster = () => {
    const element = document.getElementById("roster");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1663563624897-de8972d7ce93?w=1920')`
        }}
      />
      
      {/* Overlay */}
      <div className="hero-overlay absolute inset-0" />
      
      {/* Scanlines Effect */}
      <div className="absolute inset-0 scanlines pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        {/* Pre-title */}
        <div className="mb-6 animate-slide-in" style={{ animationDelay: '0.1s' }}>
          <span 
            className="font-orbitron text-xs md:text-sm tracking-[0.3em] uppercase font-bold"
            style={{
              background: 'linear-gradient(90deg, #00f3ff, #ff0099, #00f3ff)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'gradient-shift 3s ease infinite',
              textShadow: '0 0 20px rgba(0, 243, 255, 0.5), 0 0 40px rgba(255, 0, 153, 0.3)'
            }}
          >
            Elite Football Training Program
          </span>
        </div>

        {/* Main Logo */}
        <div className="relative inline-block mb-8 animate-slide-in" style={{ animationDelay: '0.2s' }}>
          <img 
            src="/gt-logo.webp" 
            alt="GT Training" 
            className="w-[280px] sm:w-[350px] md:w-[450px] lg:w-[550px] h-auto mx-auto relative z-10 drop-shadow-[0_0_30px_rgba(74,144,255,0.6)] drop-shadow-[0_0_60px_rgba(255,0,153,0.4)]"
            data-testid="hero-logo"
          />
        </div>

        {/* Tagline */}
        <p 
          className="font-rajdhani text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto animate-slide-in"
          style={{ animationDelay: '0.3s' }}
        >
          Transform your game. Dominate the field. 
          <span className="text-neon-blue"> No shortcuts. No excuses.</span>
        </p>

        {/* CTA Buttons */}
        <div 
          className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-slide-in"
          style={{ animationDelay: '0.4s' }}
        >
          <button 
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="cyber-btn pulse-glow"
            data-testid="hero-cta-primary"
          >
            <span className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              START TRAINING
            </span>
          </button>
          <button 
            onClick={() => document.getElementById('media')?.scrollIntoView({ behavior: 'smooth' })}
            className="cyber-btn cyber-btn-pink"
            data-testid="hero-cta-secondary"
          >
            <span>VIEW HIGHLIGHTS</span>
          </button>
        </div>

        {/* Stats */}
        <div 
          className="grid grid-cols-3 gap-8 max-w-xl mx-auto animate-slide-in"
          style={{ animationDelay: '0.5s' }}
        >
          <div className="text-center">
            <Trophy className="w-8 h-8 text-neon-pink mx-auto mb-2" />
            <div className="font-orbitron text-3xl md:text-4xl text-white mb-1">15+</div>
            <div className="font-rajdhani text-xs text-gray-500 uppercase tracking-wider">Championships</div>
          </div>
          <div className="text-center">
            <Users className="w-8 h-8 text-neon-blue mx-auto mb-2" />
            <div className="font-orbitron text-3xl md:text-4xl text-white mb-1">500+</div>
            <div className="font-rajdhani text-xs text-gray-500 uppercase tracking-wider">Athletes Trained</div>
          </div>
          <div className="text-center">
            <Zap className="w-8 h-8 text-neon-green mx-auto mb-2" />
            <div className="font-orbitron text-3xl md:text-4xl text-white mb-1">98%</div>
            <div className="font-rajdhani text-xs text-gray-500 uppercase tracking-wider">Success Rate</div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button 
        onClick={scrollToRoster}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-gray-500 hover:text-neon-blue transition-colors animate-bounce"
        data-testid="scroll-indicator"
      >
        <ChevronDown className="w-8 h-8" />
      </button>

      {/* Side Decorations */}
      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 hidden lg:block">
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-neon-blue to-transparent" />
      </div>
      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 hidden lg:block">
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-neon-pink to-transparent" />
      </div>
    </section>
  );
};

export default HeroSection;
